import React, { useState } from "react";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LOGIN } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8583e645-db93-4117-b2c4-4d2d1bbcb032/artifacts/5imn8h2x_cc.png";
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1766561994067-dbd575e1cff2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ?? JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="w-full md:w-[42%] flex flex-col justify-between bg-white px-10 py-10 lg:px-16 lg:py-12">
        <div className="animate-fade-in-up" style={{ animationDelay: "0ms" }}>
          <img src={LOGO_URL} alt="GoSmart Dashboard" className="h-16 object-contain" />
        </div>

        <div className="flex-1 flex flex-col justify-center py-10">
          <div className="mb-10 animate-fade-in-up" style={{ animationDelay: "80ms" }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-1 bg-[#FFD700]" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#475569]">
                Lead Management Portal
              </span>
            </div>
            <h1 className="text-4xl font-bold text-[#1B2A6B] leading-tight tracking-tight">
              Welcome Back
            </h1>
            <p className="mt-2 text-sm text-[#475569]">Sign in to access your workspace</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="animate-fade-in-up" style={{ animationDelay: "160ms" }}>
              <Label htmlFor="email" className="text-xs font-semibold tracking-widest uppercase text-[#1B2A6B] mb-1.5 block">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid={LOGIN.emailInput}
                placeholder="you@gosmart.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm text-[#0F172A] placeholder:text-[#94A3B8]"
                autoComplete="email"
              />
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: "240ms" }}>
              <Label htmlFor="password" className="text-xs font-semibold tracking-widest uppercase text-[#1B2A6B] mb-1.5 block">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid={LOGIN.passwordInput}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm pr-10 text-[#0F172A] placeholder:text-[#94A3B8]"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1B2A6B] transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between animate-fade-in-up" style={{ animationDelay: "320ms" }}>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-me"
                  data-testid={LOGIN.rememberMe}
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="rounded-none border-[#E2E8F0] data-[state=checked]:bg-[#1B2A6B] data-[state=checked]:border-[#1B2A6B]"
                />
                <Label htmlFor="remember-me" className="text-sm text-[#475569] cursor-pointer select-none">
                  Remember me
                </Label>
              </div>
              <a href="#" className="text-xs text-[#1B2A6B] font-semibold hover:text-[#FFD700] transition-colors tracking-wide">
                Forgot password?
              </a>
            </div>

            {error && (
              <p data-testid={LOGIN.errorMessage} className="text-xs text-red-600 bg-red-50 border-l-4 border-red-500 px-3 py-2">
                {error}
              </p>
            )}

            <div className="animate-fade-in-up" style={{ animationDelay: "400ms" }}>
              <Button
                type="submit"
                data-testid={LOGIN.submitButton}
                disabled={loading}
                className="w-full h-12 rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-sm border-b-4 border-[#FFD700] disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn size={16} />
                    Sign In
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>

        <p className="text-xs text-[#94A3B8]">
          &copy; {new Date().getFullYear()} GoSmart Dashboard. All rights reserved.
        </p>
      </div>

      {/* Right panel */}
      <div className="hidden md:block md:w-[58%] relative overflow-hidden">
        <img src={HERO_IMAGE} alt="Logistics" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A6B]/20 to-transparent pointer-events-none" />
      </div>
    </div>
  );
}
